// src/components/shell/help/modeHelpRegistry.tsx
// Single source of truth for the per-mode help content surfaced inside
// HelpPopover. Replaces the inline switch that used to live in
// ModeHelpContent.tsx (now deleted).
//
// When you change copy or import a new help component, also bump the mode's
// version in modeHelpVersions.ts so existing users get a fresh auto-open.
import type { ReactNode } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { LeadsHelp } from "@/components/lead/sections/help/LeadsHelp";
import { TrainingHelp } from "@/components/lead/sections/help/TrainingHelp";
import { DashboardHelp } from "@/components/lead/sections/help/DashboardHelp";
import { AdminHelp } from "@/components/lead/sections/help/AdminHelp";
import { AdsAssistantHelp } from "@/components/lead/sections/help/AdsAssistantHelp";
import { PlatformHelp } from "@/components/lead/sections/help/PlatformHelp";
import { getMfgHelpBody } from "./modeHelpMfgBodies";
import { getOpsHelpBody } from "./modeHelpOpsBodies";

const MODE_TITLES: Partial<Record<PrimaryMode, string>> = {
  leads: "Leads",
  // Sales-bucket Order Intake (role-scoped, post-2026-06-15 bucket move
  // per docs/products/turbo_sales.md §5).
  salesIntake: "Sales — Order Intake",
  dealerPlaceOrder: "Dealer — Place Order",
  dealerOrderHistory: "Dealer — Order History",
  opportunities: "Sales — Opportunities",
  formsAndFormats: "Manager — Documents Editor",
  salesConsole: "Sales Console",
  mfgInventory: "Manufacturing — Inventory",
  mfgModels: "Manufacturing — Models (BOM)",
  mfgPricing: "Manufacturing — COGS",
  mfgOverhead: "Manufacturing — Overhead buckets",
  mfgOrders: "Manufacturing — Orders",
  mfgPlants: "Manufacturing — Plants",
  mfgBuildPipeline: "Manufacturing — Build pipeline",
  mfgLaborBilling: "Manufacturing — Labor rates",
  mfgFulfill: "Production — Fulfill",
  mfgAudits: "Production — Audits",
  mfgInventoryCount: "Production — Inventory count",
  mfgPurchasing: "Production — Purchasing Assistant",
  mfgLaborPay: "Production — Labor pay",
  mfgBuilderAssistant: "Production — Builder Assistant",
  mfgQcDashboard: "Production — QC Dashboard",
  mfgReports: "Manufacturing — Reports",
  mfgAdminHome: "Manufacturing — Admin Home",
  logisticsRoutes: "Logistics — Routes",
  logisticsCalendar: "Logistics — Calendar",
  logisticsCarriers: "Logistics — Carriers",
  fulfillmentInventory: "Fulfillment — Inventory",
  fulfillmentOrders: "Fulfillment — Orders",
  fulfillmentIntake: "Fulfillment — Intake forms",
  fulfillmentForms: "Fulfillment — Forms",
  fulfillmentEntry: "Fulfillment — New inventory",
  serviceMaterialsUsage: "Service — Materials Usage",
  training: "Training Mode",
  knowledgebase: "Knowledge Base",
  aiPrompting: "AI Prompting",
  adsAssistant: "Ads Assistant",
  locationTools: "Location Tools",
  operations: "Deals",
  dealTypes: "Deal Types",
  rtoTerms: "RTO Terms",
  dailyChecklist: "Daily checklist",
  physicalInventory: "Physical inventory",
  opsLookup: "Lookup",
  opsObligations: "Obligations",
  adsGuidance: "Ads Guidance",
  audienceData: "Audience Data",
  coverageAssistant: "Coverage Assistant",
  admin: "Admin",
  dashboard: "Dashboard",
  dev: "Developer Bug Queue",
  platform: "Platform View — Terminology",
};

export type HelpEntryCtx = {
  salesperson: { name: string } | null;
  location: { name: string } | null;
  appDisplayName: string;
};

export type HelpEntry = {
  title: string;
  body: ReactNode;
};

export function getHelpTitle(mode: PrimaryMode, fallbackAppName: string): string {
  return MODE_TITLES[mode] ?? `How to use ${fallbackAppName}`;
}

export function getHelpBody(mode: PrimaryMode, ctx: HelpEntryCtx): ReactNode {
  switch (mode) {
    case "salesIntake":
      return (
        <div className="space-y-2">
          <p>
            <strong>Sales rep</strong> — submit a lightweight customer order (contact,
            sale path, optional manufacturer). One <strong>Submit order</strong> action
            routes to your org queue or upstream manufacturer review automatically.
            Lead Workspace stays the voice-first capture surface; use Order Intake when
            you are ready to promote the deal. From a captured lead, use{" "}
            <strong>Submit as deal →</strong> to open this form with contact and product
            fields pre-filled.
          </p>
          <p>
            <strong>Ops review</strong> — paperwork-check queue for incoming intake rows.
            Review the customer snapshot and checklist, then Accept (paperwork OK), Hold
            (ask for fixes), or Cancel. Post-acceptance deal paperwork stays in Ops →{" "}
            <code>operations</code>.
          </p>
          <p>
            <strong>Manufacturer</strong> — accept-to-pipeline with optional plant
            override. Multi-variant users switch via <em>View as</em> tabs at the top.
          </p>
        </div>
      );
    case "salesConsole":
      return (
        <p>
          Daily control surface for <strong>follow-up/disposition priorities</strong>, bulk lead intake, and quick
          RTO quoting. Use <strong>View this lead</strong> from console rows to jump into Leads mode for full editing.
        </p>
      );
    case "opportunities":
      return (
        <div className="space-y-2">
          <p>
            <strong>Opportunities</strong> are customer-associated sales opportunities scoped
            to a product type. Create one per project, capture structured pricing, upload
            supporting documents, track revisions, and generate a branded proposal package PDF
            with your organization&apos;s logo in the header. Every revision pins an immutable
            pricing snapshot so any generated proposal is reproducible from data, not from
            edited PDFs.
          </p>
          <p>
            Operator guide: <code>docs/opportunity_engine.md</code>.
          </p>
        </div>
      );
    case "formsAndFormats":
      return (
        <div className="space-y-2">
          <p>
            <strong>Documents Editor</strong> is the manager authoring surface for org documents,
            proposals, sign templates, and reusable assets. Use <strong>Documents</strong> to browse
            by category, fill and export PDFs, or author new forms and templates with the inline
            editor.
          </p>
          <p>
            <strong>Proposals</strong> configures the opportunity proposal package — body copy,
            calculation lines, deposit rules, payment milestones, document upload slots, and
            per-format contact overrides per product type.
          </p>
          <p>
            <strong>Asset library</strong> stores logos, signatures, and images reused across
            document blocks. Plan: <code>docs/planning/document_builder_module_plan.md</code>.
          </p>
        </div>
      );
    case "mfgBuildPipeline":
    case "mfgLaborBilling":
    case "mfgInventory":
    case "mfgModels":
    case "mfgPricing":
    case "mfgOverhead":
    case "mfgOrders":
    case "mfgPlants":
    case "mfgReports":
    case "mfgAdminHome":
    case "mfgFulfill":
    case "mfgAudits":
    case "mfgInventoryCount":
    case "mfgPurchasing":
    case "mfgLaborPay":
    case "mfgBuilderAssistant":
    case "mfgQcDashboard":
      return getMfgHelpBody(mode);
    case "logisticsRoutes":
    case "logisticsCalendar":
    case "logisticsCarriers":
    case "fulfillmentForms":
    case "fulfillmentOrders":
    case "fulfillmentInventory":
    case "fulfillmentIntake":
    case "fulfillmentEntry":
      return getOpsHelpBody(mode);
    case "training":
      return <TrainingHelp />;
    case "knowledgebase":
      return (
        <div className="space-y-2">
          <p>
            Search, review, import, and update KB articles from one place. Trainer-and-up roles can work here without the
            legacy developer login.
          </p>
          <p>
            New articles default to <strong>org</strong> scope for the active organization. Manufacturer or app scope is
            for truly shared libraries; mis-filed org-only content there can appear too widely in retrieval — use scope and
            owning org intentionally.
          </p>
        </div>
      );
    case "aiPrompting":
      return (
        <p>
          Platform-level prompt visibility panel for auditing where AI prompts live today. Access is restricted to{" "}
          <strong>platform_dev</strong> and <strong>platform_owner</strong>.
        </p>
      );
    case "adsAssistant":
      return <AdsAssistantHelp />;
    case "coverageAssistant":
      return (
        <p>
          Drop a delivery address or a city and see which providers cover it. Preferred rings read stronger than the
          full territory. Each result links to that provider&apos;s pricing program. Open the pin in Google Maps when
          you need the street.
        </p>
      );
    case "audienceData":
    case "operations":
    case "dealTypes":
    case "dailyChecklist":
    case "physicalInventory":
    case "opsLookup":
    case "opsObligations":
      return getOpsHelpBody(mode);
    case "locationTools":
      return (
        <p>
          Configure <strong>location quick links</strong> (org defaults and per-lot URLs) used in the Leads
          &quot;Location links&quot; panel. Sales, trainer, marketing, admin, and owner roles can open this mode.
          Saved link sets are stored on this device per organization until server sync is added.
        </p>
      );
    case "admin":
      return <AdminHelp />;
    case "dashboard":
      return <DashboardHelp />;
    case "dev":
      return (
        <p>
          Review bug reports and the <strong>Dev sync queue</strong> (platform_owner / platform_dev). Owner notes sync
          directly; dev notes need owner approve/alter/deny. Optional SVG + target path on Report a bug. Desktop:{" "}
          <code className="text-xs">docs/runbooks/dev_sync_and_fix.md</code>.
        </p>
      );
    case "platform":
      return <PlatformHelp />;
    case "leads":
    default:
      return (
        <LeadsHelp
          salesperson={ctx.salesperson}
          location={ctx.location}
          appDisplayName={ctx.appDisplayName}
        />
      );
  }
}

export function getHelpEntry(mode: PrimaryMode, ctx: HelpEntryCtx): HelpEntry {
  return {
    title: getHelpTitle(mode, ctx.appDisplayName),
    body: getHelpBody(mode, ctx),
  };
}
